import React from 'react';
import { useIntl } from 'react-intl';
import { MapPin, MapTrifold } from '@phosphor-icons/react';
import { fixMojibake } from '../../../../../../utils/text/fix-mojibake';
import { buildSrpFacetHref } from '../../utils/buildSrpFacetHref';
import { brandLinkHoverHandlers } from './brand-hover';
import { useFormatMessage } from '../../../../../../i18n-review/client/Msg';
import { useDocumentDarkClass } from '../../../../../../hooks/useDocumentDarkClass';
import { brandAccentOnDark } from '../../../../../../utils/brand-color/adjust-brand-color';
import type { StructuredRemoteScope } from '../../../../../../utils/location/structured-job-location-display';

export interface EditorialHeadlineFactsProps {
  location?: string;
  /** 0 when there's no geocoded company address to show; the Map chip is omitted entirely rather than opening an empty modal. */
  locationCount: number;
  onOpenMap: () => void;
  remoteness?: string;
  /**
   * TEC-482 (new location system on): every structured remote scope, rendered right after the
   * work model as "Etätyö (EMEA)", or "Remote (Germany, Ireland, Spain, United Kingdom)" for a job
   * that lists several discrete remote countries. Each scope links to its matching `regionTags`
   * search when it has one (a continent, recruiting region or country); a global scope is plain
   * text. Empty/undefined: the plain remoteness link, unchanged.
   */
  remoteScopes?: StructuredRemoteScope[];
  jobType?: string;
  normalisedRoleLevel?: string;
  /** Already-formatted monthly-EUR range (e.g. "€5.2–6.4k / mo"), regardless of provenance; see JobDetailPageV2.tsx's displaySalaryLabel. */
  displaySalaryLabel?: string | null;
  /** Company's real brand colour; used for the location pin, Map chip, remoteness link hover, and salary figure instead of the generic jc-accent green. */
  brandColor?: string;
  /** Shown only when >1 — a single position isn't worth calling out. */
  numberOfPositions?: number;
  /** Client-only subline for the Map chip, e.g. "24 min from home"; only set once a travel time from the user's saved home address is known (see useHomeCommute). */
  mapChipNote?: string | null;
}

/**
 * TEC-512 editorial-rail redesign: the single compact "at a glance" facts line replacing the old
 * four-module header; location leads and is visually largest (Space Grotesk, larger than the
 * rest of the row), then a "Map" chip opening JobLocationMapModal, then remoteness, then job
 * type + role level, then salary (the largest colored figure on the line after the location).
 * Matches the design-review mockup's actual `.headline-facts` content; posted date isn't
 * repeated here since it's already shown prominently in the brand rail's dates block.
 */
export function EditorialHeadlineFacts({
  location,
  locationCount,
  onOpenMap,
  remoteness,
  remoteScopes = [],
  jobType,
  normalisedRoleLevel,
  displaySalaryLabel,
  brandColor,
  numberOfPositions,
  mapChipNote = null,
}: EditorialHeadlineFactsProps): React.ReactElement | null {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const facetHref = (facetKey: string, value: string) => buildSrpFacetHref(intl.locale, facetKey, value);
  const isDark = useDocumentDarkClass();
  // The brand panel colour is a background colour (near-black for every neutral-fallback brand)
  // and disappears as text/icon on the dark card; use the dark-surface accent there instead.
  const accentColor = brandColor ? (isDark ? brandAccentOnDark(brandColor) : brandColor) : undefined;
  const facetLinkClass = `underline decoration-current/30 underline-offset-2 ${brandColor ? '' : 'hover:text-jc-accent'}`;

  const jobTypeAndLevel = [jobType, normalisedRoleLevel].filter(Boolean).join(', ');

  // Scope without a work-model value (rare, but "Remote - EMEA" jobs whose remoteness wasn't
  // extracted exist): show the scope(s) on their own in the remoteness slot rather than dropping it.
  const remotenessText = remoteness || remoteScopes.map((s) => s.label).join(', ') || '';

  if (!location && !remotenessText && !jobTypeAndLevel && !displaySalaryLabel) return null;

  const hasPositions = Boolean(numberOfPositions && numberOfPositions > 1);
  const hasSmallFacts = Boolean(remotenessText || jobTypeAndLevel || hasPositions);
  const smallFactClass = 'font-bold text-[#4A4438] dark:text-[#C4A882]';
  // One entry: identical markup to the old single-scope version. 2+: each scope keeps its own
  // facet link (or plain text for a global scope), comma-joined, e.g. "Remote (Germany, Ireland)".
  const scopeNode =
    remoteScopes.length > 0 ? (
      <>
        {remoteScopes.map((scope, index) => (
          <React.Fragment key={`${scope.scopeType}-${scope.regionTagValue ?? scope.label}`}>
            {index > 0 ? ', ' : null}
            {scope.regionTagValue ? (
              <a
                href={facetHref('regionTags', scope.regionTagValue)}
                className={`${smallFactClass} ${facetLinkClass}`}
                {...brandLinkHoverHandlers(brandColor)}
              >
                {fixMojibake(scope.label)}
              </a>
            ) : (
              <span className={smallFactClass}>{fixMojibake(scope.label)}</span>
            )}
          </React.Fragment>
        ))}
      </>
    ) : null;
  const sepClass = 'text-[rgba(22,21,15,0.2)] dark:text-[#4A3520]';

  // Mobile is the main audience and a single flex-wrap row broke there (a chip splitting the
  // line, separators dangling at line ends, the salary orphaned). Below `sm` this is three
  // deliberate lines: location + Map chip, the small facts, then the salary. From `sm` up the
  // groups dissolve (`sm:contents`) back into the one editorial row, separators included.
  return (
    <div className="flex flex-col gap-2 border-b border-[rgba(22,21,15,0.08)] dark:border-[#4A3520]/70 pb-3.5 text-[13px] sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-3 sm:gap-y-1.5">
      {location || locationCount > 0 ? (
        <div className="flex min-w-0 flex-wrap items-center gap-2 sm:contents">
          {location ? (
            <span className="inline-flex items-center gap-1.5 [font-family:'Space_Grotesk',sans-serif] text-[15.5px] font-bold text-[#16150F] dark:text-[#F3EFE4]">
              <MapPin
                className={`h-[15px] w-[15px] flex-none ${accentColor ? '' : 'text-jc-accent'}`}
                style={accentColor ? { color: accentColor } : undefined}
                weight="duotone"
                aria-hidden
              />
              {fixMojibake(location)}
            </span>
          ) : null}
          {locationCount > 0 ? (
            // Same pill language as the "Watch role" button beside the title (RoleFollowButton):
            // one line, hairline border, icon + label. The commute figure or site count rides
            // along as muted text inside the pill instead of a second line.
            <button
              type="button"
              onClick={onOpenMap}
              className="inline-flex items-center gap-1.5 rounded-full border border-[rgba(22,21,15,0.14)] dark:border-[#4A3520] bg-transparent px-2.5 py-1 text-xs font-semibold text-[#16150F] dark:text-[#F3EFE4] cursor-pointer transition-colors hover:bg-[rgba(22,21,15,0.04)] dark:hover:bg-white/5"
            >
              <MapTrifold className={`h-3.5 w-3.5 flex-none ${accentColor ? '' : 'text-jc-accent'}`} style={accentColor ? { color: accentColor } : undefined} weight="duotone" aria-hidden />
              <span>{formatMessage({ id: 'jobDetailPageV2.mapChip', defaultMessage: 'Map', description: 'Chip button opening the job location map modal, editorial-rail redesign' })}</span>
              {mapChipNote ? (
                <span className="font-medium text-[#8C8474] whitespace-nowrap">· {mapChipNote}</span>
              ) : locationCount > 1 ? (
                <span className="font-medium text-[#8C8474] whitespace-nowrap">
                  ·{' '}
                  {formatMessage(
                    { id: 'jobDetailPageV2.mapChip.locationCount', defaultMessage: '{count} locations', description: 'Location count under the Map chip, editorial-rail redesign' },
                    { count: locationCount },
                  )}
                </span>
              ) : null}
            </button>
          ) : null}
        </div>
      ) : null}
      {hasSmallFacts ? (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 sm:contents">
          {remotenessText ? (
            <>
              <span className={`hidden sm:inline ${sepClass}`} aria-hidden>·</span>
              {remoteness ? (
                <a
                  href={facetHref('remoteness', remoteness)}
                  className={`${smallFactClass} ${facetLinkClass}`}
                  {...brandLinkHoverHandlers(brandColor)}
                >
                  {fixMojibake(remoteness)}
                </a>
              ) : null}
              {remoteness && scopeNode ? (
                <span className={`inline-flex items-center gap-0.5 ${smallFactClass}`}>
                  <span aria-hidden>(</span>
                  {scopeNode}
                  <span aria-hidden>)</span>
                </span>
              ) : (
                scopeNode
              )}
            </>
          ) : null}
          {jobTypeAndLevel ? (
            <>
              <span className={`${remotenessText ? '' : 'hidden sm:inline'} ${sepClass}`} aria-hidden>·</span>
              <span className={smallFactClass}>{fixMojibake(jobTypeAndLevel)}</span>
            </>
          ) : null}
          {hasPositions ? (
            <>
              <span className={`${remotenessText || jobTypeAndLevel ? '' : 'hidden sm:inline'} ${sepClass}`} aria-hidden>·</span>
              <span className="font-bold text-[#4A4438] dark:text-[#C4A882]">
                {formatMessage(
                  { id: 'jobDetailPageV2.numberOfPositions', defaultMessage: '{count} openings', description: 'Number of open positions for this listing, shown in the headline facts row, editorial-rail redesign' },
                  { count: numberOfPositions },
                )}
              </span>
            </>
          ) : null}
        </div>
      ) : null}
      {displaySalaryLabel ? (
        <div className="sm:contents">
          <span className={`hidden sm:inline ${sepClass}`} aria-hidden>·</span>
          <span
            className={`[font-family:'Space_Grotesk',sans-serif] font-bold text-[17px] ${accentColor ? '' : 'text-jc-accent'}`}
            style={accentColor ? { color: accentColor } : undefined}
          >
            {displaySalaryLabel}
          </span>
        </div>
      ) : null}
    </div>
  );
}
